import { Modal, toast } from '@cma/common'
import { useRestoreBrokerAccount } from '@cma/features/admin/broker'
import { HelixIcon } from '@helix/helix-icon'
import { spinner as SpinnerIcon } from '@helix/helix-icon/outlined'
import { useState } from 'react'

interface RestoreAccountProps {
  id: string
  name: string
  isOpen: boolean
  onClose: () => void
  onSuccess?: () => void
}

export function RestoreAccount({
  id,
  name,
  isOpen,
  onClose,
  onSuccess
}: RestoreAccountProps) {
  const { mutate: restoreAccount, isLoading } = useRestoreBrokerAccount()
  const [_name, setName] = useState('')

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        if (!isLoading) {
          onClose()
        }
      }}>
      <Modal.Title weight={600}>Restore Account?</Modal.Title>
      <form
        className="mt-4 space-y-6"
        onSubmit={(e) => {
          e.preventDefault()
          restoreAccount(
            { id },
            {
              onSuccess(data) {
                if (data.restoreBrokerAccount?.errors?.length) {
                  toast(data.restoreBrokerAccount.errors.join(', '), {
                    variant: 'error'
                  })
                  return
                }

                toast('Broker account restored successfully!', {
                  variant: 'success'
                })
                onClose()
                onSuccess?.()
              },
              onError() {
                toast(
                  'Could not restore the broker account. Please try again.',
                  {
                    variant: 'error'
                  }
                )
              }
            }
          )
        }}>
        <p className="helix-body helix-text-gray-800">
          Are you sure you want to restore the <strong>{name}</strong> Broker
          Account? They will regain everything: Users, themes, and everything
          associated with this broker account.
        </p>
        <p className="helix-body helix-text-gray-800">
          If you're sure, confirm below.
        </p>
        <div>
          <label
            className="helix-body--strong helix-mb-1 helix-d-inline-block helix-text-gray-700"
            htmlFor="name">
            Enter Broker name exactly as written above to confirm restoration
          </label>
          <input
            id="name"
            className="helix-text-input"
            placeholder={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <div className="-mx-6 flex justify-end gap-3 border-t border-solid border-gray-200 px-6 pt-6">
          <button
            className="helix-btn helix-btn--tertiary"
            type="button"
            onClick={onClose}>
            Cancel
          </button>
          <button
            disabled={_name !== name || isLoading}
            className="helix-btn helix-btn--primary helix-d-flex helix-align--center helix-justify--center gap-1 disabled:cursor-not-allowed disabled:opacity-50">
            {isLoading && (
              <span className="mx-auto block h-5 w-5 animate-spin">
                <HelixIcon icon={SpinnerIcon} />
              </span>
            )}
            <span>Restore Account</span>
          </button>
        </div>
      </form>
    </Modal>
  )
}
